import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FaExclamationCircle } from 'react-icons/fa';
import { useTitle } from '../../../context/TitleContext';
import apiClient from '../../../shared/utils/apiConfig';
import { showErrorToast } from '../../../shared/utils/toastHelper';
import EnhancedPagination from '../../../shared/components/EnhancedPagination';

const extractComplaintsArray = (payload) => {
    if (!payload) return [];

    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    if (Array.isArray(payload?.result)) {
        return payload.result;
    }

    const resultNode = payload?.result ?? payload?.data ?? payload;

    if (Array.isArray(resultNode)) {
        return resultNode;
    }

    if (Array.isArray(resultNode?.data)) {
        return resultNode.data;
    }

    if (Array.isArray(payload?.complaints)) {
        return payload.complaints;
    }

    if (Array.isArray(payload?.data?.data)) {
        return payload.data.data;
    }

    return [];
};

const getComplaintId = (complaint) =>
    complaint?.id ??
    complaint?.complaint_id ??
    complaint?.branch_complaint_id ??
    complaint?.complaintId ??
    null;

const getBranchName = (complaint) => {
    if (!complaint) return '-';
    const branch =
        complaint.branch ||
        complaint.branch_data ||
        complaint.store_branch ||
        complaint.clinic_branch ||
        complaint.branch_info;

    if (branch) {
        return (
            branch.title_ar ||
            branch.title_en ||
            branch.name ||
            branch.name_ar ||
            branch.market_name ||
            branch.branch_name ||
            branch.title ||
            branch?.name_en ||
            branch?.branchTitle ||
            '-'
        );
    }

    const fallbackTitle =
        complaint.branch_title ||
        complaint.branch_name ||
        complaint.branch ||
        complaint.store_branch_name ||
        complaint.clinic_branch_name ||
        null;

    if (fallbackTitle) {
        return fallbackTitle;
    }

    if (complaint.branch_id) {
        return `فرع رقم ${complaint.branch_id}`;
    }

    return '-';
};

const getReporterName = (complaint) => {
    if (!complaint) return '-';
    const reporter =
        complaint.reporter ||
        complaint.user ||
        complaint.client ||
        complaint.created_by ||
        complaint.sender;

    if (reporter) {
        return reporter.name || reporter.full_name || reporter.username || reporter.email || '-';
    }

    return (
        complaint.reporter_name ||
        complaint.client_name ||
        complaint.user_name ||
        complaint.sender_name ||
        '-'
    );
};

const getComplaintBody = (complaint) =>
    complaint?.complaint ||
    complaint?.description ||
    complaint?.message ||
    complaint?.details ||
    complaint?.body ||
    complaint?.text ||
    '-';

const getCreatedAt = (complaint) =>
    complaint?.created_at ||
    complaint?.createdAt ||
    complaint?.created_at_formatted ||
    complaint?.createdAtFormatted ||
    complaint?.date ||
    complaint?.created_date ||
    complaint?.submitted_at ||
    null;

const AdminBranchComplaints = () => {
    const { setTitle } = useTitle();
    const [complaints, setComplaints] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState(null);

    const fetchComplaints = useCallback(async (page = 1) => {
        try {
            setLoading(true);
            const response = await apiClient.get('/branch_complaints', {
                params: { page }
            });
            const payload = response?.data ?? response;
            const complaintsArray = extractComplaintsArray(payload);
            setComplaints(Array.isArray(complaintsArray) ? complaintsArray : []);

            const paginationNode = payload?.result ?? payload?.data;
            if (paginationNode && typeof paginationNode === 'object') {
                setPagination({
                    current_page: paginationNode.current_page ?? page,
                    last_page: paginationNode.last_page ?? 1,
                    per_page: paginationNode.per_page ?? complaintsArray?.length ?? 0,
                    total: paginationNode.total ?? complaintsArray?.length ?? 0,
                    from: paginationNode.from ?? 0,
                    to: paginationNode.to ?? complaintsArray?.length ?? 0
                });
            } else {
                setPagination(null);
            }

            setLastUpdated(new Date());
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                'فشل في جلب الشكاوى';
            showErrorToast(errorMessage);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        setTitle('شكاوى الفروع');
    }, [setTitle]);

    useEffect(() => {
        fetchComplaints(currentPage);
    }, [currentPage, fetchComplaints]);

    const preparedComplaints = useMemo(
        () =>
            complaints.map((complaint) => ({
                ...complaint,
                _id: getComplaintId(complaint),
                branchName: getBranchName(complaint),
                reporterName: getReporterName(complaint),
                complaintBody: getComplaintBody(complaint),
                createdAt: getCreatedAt(complaint)
            })),
        [complaints]
    );

    const handlePageChange = (page) => {
        if (!pagination) return;
        const last = pagination.last_page ?? 1;
        if (page < 1 || page > last || page === (pagination.current_page ?? 1)) {
            return;
        }
        setCurrentPage(page);
    };

    return (
        <div className="main_content admin-branch-complaints-page">
            <div className="dashboard-container">
             

                <div className="card shadow-lg border-0">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">جاري التحميل...</span>
                                </div>
                                <p className="mt-3 text-muted">جاري جلب الشكاوى...</p>
                            </div>
                        ) : preparedComplaints.length === 0 ? (
                            <div className="text-center py-5">
                                <FaExclamationCircle className="empty-icon mb-3" />
                                <h5 className="fw-semibold mb-2">لا توجد شكاوى بعد</h5>
                                <p className="text-muted mb-0">لن تظهر أي بيانات هنا حتى يقوم أحد الفروع بإرسال شكوى.</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-modern">
                                        <thead>
                                            <tr>
                                                <th className="text-center">#</th>
                                                <th>الفرع</th>
                                                <th>صاحب الشكوى</th>
                                                <th className="text-center d-none d-lg-table-cell">رقم التواصل</th>
                                                <th>وصف الشكوى</th>
                                                <th className="text-center">تاريخ الإنشاء</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {preparedComplaints.map((complaint, index) => (
                                                <motion.tr
                                                    key={complaint._id ?? index}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: index * 0.03 }}
                                                    className="table-row-modern"
                                                >
                                                    <td className="text-center">
                                                        <div className="order-number-badge">#{index + 1}</div>
                                                    </td>
                                                    <td>
                                                        <span className="branch-name">{complaint.branchName || '-'}</span>
                                                    </td>
                                                    <td>
                                                        <span className="reporter-name">{complaint.reporterName || '-'}</span>
                                                    </td>
                                                    <td className="text-center d-none d-lg-table-cell">
                                                        <span className="reporter-phone">
                                                            {complaint?.client?.phone ||
                                                                complaint?.reporter?.phone ||
                                                                complaint?.user?.phone ||
                                                                '-'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="complaint-body">
                                                            {complaint.complaintBody || '-'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center text-muted">
                                                        {complaint.createdAt
                                                            ? new Date(complaint.createdAt).toLocaleString('ar-EG')
                                                            : '-'}
                                                    </td>
                                                </motion.tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {pagination && (pagination.total ?? preparedComplaints.length) > 0 && (
                                    <div className="mt-4">
                                        <EnhancedPagination
                                            currentPage={pagination.current_page || currentPage}
                                            totalPages={pagination.last_page || 1}
                                            onPageChange={handlePageChange}
                                            totalItems={pagination.total || preparedComplaints.length}
                                            itemsPerPage={pagination.per_page || preparedComplaints.length}
                                            className="mt-3"
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <style jsx>{`
                .admin-branch-complaints-page .icon-container {
                    width: 42px;
                    height: 42px;
                    border-radius: 12px;
                    background: linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(236, 72, 153, 0.15));
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #ef4444;
                    font-size: 1.25rem;
                }

                .admin-branch-complaints-page .empty-icon {
                    font-size: 2.5rem;
                    color: #9ca3af;
                }

                .admin-branch-complaints-page .order-number-badge {
                    background: rgba(24, 65, 148, 0.08);
                    color: #184194;
                    padding: 0.35rem 0.75rem;
                    border-radius: 999px;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.85rem;
                }

                .admin-branch-complaints-page .branch-name,
                .admin-branch-complaints-page .reporter-name {
                    font-weight: 600;
                    color: #1f2937;
                }

                .admin-branch-complaints-page .complaint-body {
                    color: #4b5563;
                    font-size: 0.95rem;
                    line-height: 1.6;
                    max-width: 420px;
                    white-space: pre-wrap;
                    word-break: break-word;
                }

                .admin-branch-complaints-page .reporter-phone {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0.35rem 0.75rem;
                    border-radius: 999px;
                    background: rgba(14, 165, 233, 0.12);
                    color: #0369a1;
                    font-weight: 600;
                    direction: ltr;
                    min-width: 135px;
                }


            `}</style>
        </div>
    );
};

export default AdminBranchComplaints;


